import * as React from 'react'

import { type UserTraits } from '@segment/analytics-next'

import { SEGMENT_API_KEY, IS_PROD } from '@webapp/infrastructure/envs'
import { type SegmentAnalyticsMetadata, type User } from '@webapp/models'
import { injectSegment } from './utils'

export interface IGlobalAnalyticsProps {
  user: User
  segmentAnalyticsMetadata: SegmentAnalyticsMetadata
}

export const GlobalAnalytics: React.FC<IGlobalAnalyticsProps> = React.memo(
  ({ user, segmentAnalyticsMetadata }: IGlobalAnalyticsProps) => {
    const [isSegmentInjected, setIsSegmentInjected] = React.useState(false)

    const hasIdentifiedRef = React.useRef(false)

    React.useEffect(() => {
      if (!user?.id) {
        return
      }

      if (injectSegment(SEGMENT_API_KEY, IS_PROD)) {
        setIsSegmentInjected(true)
      }
    }, [user?.id])

    React.useEffect(() => {
      const analytics = window.analytics

      // Prevent duplicate identification even if metadata changes after the initial identify call.
      // This can happen if the user updates their profile, which would update the metadata and trigger this effect again.
      if (!isSegmentInjected || !analytics || hasIdentifiedRef.current) {
        return
      }

      const { user: analyticsUser, group } = segmentAnalyticsMetadata

      // Segment's UserTraits 'company' field expects an object, but our data structure uses a string.
      // Our SegmentAnalyticsUserMetaData overrides 'company' as a string to match our data structure.
      analytics.identify(analyticsUser.id, analyticsUser.metadata as UserTraits)
      analytics.group(group.id, group.metadata)

      hasIdentifiedRef.current = true
    }, [isSegmentInjected, segmentAnalyticsMetadata])

    return null // Analytics-only component, renders no UI
  }
)

GlobalAnalytics.displayName = 'GlobalAnalytics'
